import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { slidesPerViewForBreakpoints } from '@bluheadless/ui-logic/src/utils/commons'
import { useUser } from '@bluheadless/ui-logic/src/providers/user'
import { useProductReviews } from '@bluheadless/ui-logic/src/hooks/product/useProductReviews'
import { CardRating } from '../../molecules/card'
import { forwardRef, useState } from 'react'
import { Root, Title, Summary, Head, ButtonAddReview } from './reviews.styled'
import round from 'lodash/round'
import SkeletonCard from '../../molecules/skeleton/card/skeleton-card'
import { Slide } from '../../particles/slider'
import { useIntl } from 'react-intl'
import { useTheme } from '@mui/system'
import Carousel from '../../organisms/carousel'
import { cx } from '@emotion/css'
import DialogReview from './dialog-review'
import ReadMore from '../../particles/read-more'
import Typography from '../../atoms/typography'
import useMediaQuery from '@mui/material/useMediaQuery'

const COLUMN_GAP = 24

const Reviews = forwardRef(function Reviews({ className }, ref) {
	const { formatMessage } = useIntl()
	const theme = useTheme()
	const [open, setOpen] = useState(false)
	const [{ isLoggedIn }] = useUser()
	const smUp = useMediaQuery(theme.breakpoints.up('sm'))

	const {
		catalog: {
			products: {
				rating: {
					enabled,
					guest: guestEnabled,
					slider: { slidesPerView: _slidesPerView },
				},
			},
		},
	} = useConfig()

	const productContextValue = useProductContext()

	const [
		{
			product: {
				id: productId,
				reviews: reviewsFallback,
				rating: { summary },
			},
		} = {},
	] = productContextValue

	const { reviews, loading: loadingReviews } = useProductReviews({
		where: {
			entity_pk_value: productId.toString(),
		},
		options: { fallbackData: reviewsFallback },
		paused: !enabled,
	})

	const slidesPerView = slidesPerViewForBreakpoints(_slidesPerView, theme.breakpoints)

	const addReviewEnabled = isLoggedIn || guestEnabled

	const responsiveProps = Object.entries(_slidesPerView)
		.map(([breakpoint, value]) => [
			breakpoint,
			{
				columns: round(parseFloat(value)),
				columnGap: COLUMN_GAP + 'px',
			},
		])
		.reduce((acc, [breakpoint, value]) => {
			return { ...acc, [breakpoint]: value }
		}, {})

	return (
		enabled && (
			<>
				<Root className={cx('Reviews-root', className)} ref={ref}>
					<Head>
						<Title variant="h2">
							{formatMessage({ id: 'reviews_title' })} <Summary>({summary || 0})</Summary>
						</Title>
						{addReviewEnabled ? (
							<ButtonAddReview color="primary" onClick={() => setOpen(true)} variant={smUp ? 'contained' : 'text'}>
								{formatMessage({ id: 'reviews_button' })}
							</ButtonAddReview>
						) : null}
					</Head>
					{loadingReviews ? (
						<SkeletonCard cardRatio={1} responsiveProps={responsiveProps} />
					) : (
						<>
							{reviews?.length > 0 ? (
								<Carousel
									navigation={false}
									pagination
									spaceBetween={COLUMN_GAP}
									slidesPerView={slidesPerView.xs}
									breakpoints={slidesPerView}
									subContainerProps={{ style: { padding: 0 } }}
								>
									{reviews.map(({ description, user, value }, index) => (
										<Slide key={index}>
											<CardRating
												ratingValue={value || 1}
												image={null}
												imageText={user}
												imageHeight={44}
												imageWidth={44}
												description={
													<ReadMore
														readMoreLabel={' [...]'}
														showLessLabel={' [' + formatMessage({ id: 'show_less' }) + ']'}
													>
														{description}
													</ReadMore>
												}
											/>
										</Slide>
									))}
								</Carousel>
							) : (
								<Typography variant="subtitle2">{formatMessage({ id: 'no_review_available' })}</Typography>
							)}
						</>
					)}
				</Root>

				{addReviewEnabled && (
					<DialogReview
						open={open}
						onSubmit={() => setOpen(false)}
						onClose={() => setOpen(false)}
						productId={productId}
					/>
				)}
			</>
		)
	)
})

export default Reviews
